import { ProductResponse } from 'lib/services/models/content-response';
import useDictionary from 'lib/hooks/useDictionary';

import styles from './ProductAllergenStatement.module.scss';

export interface ProductAllergenStatementProps {
  product?: ProductResponse;
  allergenClaims?: AllergenClaimItem[] | null;
  maxClaimsCount?: number;
}

type IngredientClaim = {
  code: string;
  qualifier?: string | null;
  claim: string;
};

export type AllergenClaimItem = {
  id?: string;
  url?: string;
  name?: string;
  displayName?: string;
  fields?: {
    code?: { value?: string } | null;
    claim?: { value?: string } | null;
  } | null;
};

type AllergenClaimFields = NonNullable<AllergenClaimItem['fields']>;

function isIngredientClaim(value: unknown): value is IngredientClaim {
  return (
    typeof value === 'object' &&
    value !== null &&
    'code' in value &&
    typeof (value as { code?: unknown }).code === 'string'
  );
}

export function ProductAllergenStatement({
  product,
  allergenClaims,
  maxClaimsCount,
}: ProductAllergenStatementProps) {
  const { getDictionaryValue } = useDictionary();

  const ingredientClaimsRaw = (product?.ProductReferenceOnly?.ingredientClaims ?? []) as unknown;

  const ingredientClaimCodes: string[] = Array.isArray(ingredientClaimsRaw)
    ? ingredientClaimsRaw
        .map((c) => {
          if (typeof c === 'string') return c;
          if (isIngredientClaim(c)) return c.code;
          return undefined;
        })
        .filter((value): value is string => typeof value === 'string' && value.length > 0)
    : [];

  const claimsArray: AllergenClaimFields[] = (allergenClaims ?? [])
    .map((data) => data.fields)
    .filter(Boolean) as AllergenClaimFields[];

  const matchedClaims: string[] = claimsArray
    .filter((c1) => {
      const code = c1.code?.value;
      return !!code && ingredientClaimCodes.includes(code);
    })
    .map((c) => c.claim?.value)
    .filter(Boolean) as string[];

  const limitedClaims =
    typeof maxClaimsCount === 'number' && maxClaimsCount > 0
      ? matchedClaims.slice(0, maxClaimsCount)
      : matchedClaims;

  const allergenStatement = product?.AllergenStatement;
  const allergenheadline =
    getDictionaryValue(
      'Components.Product.ProductAllergenStatement.ProductAllergenStatementHeadline'
    ) || 'Allergy Information';
  const allergenFallback =
    getDictionaryValue(
      'Components.Product.ProductAllergenStatement.ProductAllergenStatementFallback'
    ) || 'Information not available';

  return (
    <div className={styles.productAllergenStatementContainer}>
      <h2 className={styles.productAllergenStatementHeading}>{allergenheadline}</h2>
      <p className={styles.productAllergenStatementText}>{allergenStatement || allergenFallback}</p>

      {limitedClaims.length > 0 && (
        <ul className={styles.productAllergenStatementList}>
          {limitedClaims.map((claim, index) => (
            <li key={index} className={styles.productAllergenStatementTextForNAF}>
              {claim}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
